import { useMemo } from 'react'
import { ArrowRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { formatAmount, cn } from '@/lib/utils'
import { computeSettlement, type PayerSummary } from '@/lib/settlement'
import { aggregateByParentCategory } from '@/lib/statistics'
import { DEFAULT_CATEGORY_COLOR, UNCATEGORIZED_LABEL } from '@/lib/constants'
import { useCategories } from '@/lib/hooks'
import type { Transaction, Category } from '@/lib/hooks'

interface TagSettlementProps {
  transactions: Transaction[]
}

// 支払者バーは「人」を表すためモノクローム階調を使い、カテゴリ色との誤認を防ぐ。
const PAYER_SHADE_CLASSES = [
  'bg-foreground',
  'bg-foreground/60',
  'bg-foreground/40',
  'bg-foreground/25',
  'bg-foreground/15',
] as const

function payerShade(index: number): string {
  return PAYER_SHADE_CLASSES[Math.min(index, PAYER_SHADE_CLASSES.length - 1)]
}

function displayName(p: { name: string | null; paidBy: string }): string {
  return p.name ?? '未設定'
}

function initial(name: string | null): string {
  return name?.charAt(0) ?? '?'
}

export function TagSettlement({ transactions }: TagSettlementProps) {
  const result = useMemo(() => computeSettlement(transactions), [transactions])
  const { data: categories = [] } = useCategories()

  if (result.payers.length === 0 || result.total === 0) return null

  return (
    <div className="space-y-3">
      <PaymentRatioCard
        payers={result.payers}
        total={result.total}
        transactions={transactions}
        categories={categories}
      />
      {result.payers.length >= 2 && (
        <SettlementCard payers={result.payers} transfers={result.transfers} />
      )}
    </div>
  )
}

function PaymentRatioCard({
  payers,
  total,
  transactions,
  categories,
}: {
  payers: PayerSummary[]
  total: number
  transactions: Transaction[]
  categories: Category[]
}) {
  const categoryRows = useMemo(() => {
    const colorMap = new Map<string, string>()
    for (const cat of categories) {
      if (cat.parent_id === null && cat.color) colorMap.set(cat.name, cat.color)
    }
    const totals = aggregateByParentCategory(transactions)
    return Array.from(totals.entries())
      .map(([name, amount]) => ({
        name,
        amount,
        color:
          name === UNCATEGORIZED_LABEL
            ? DEFAULT_CATEGORY_COLOR
            : colorMap.get(name) ?? DEFAULT_CATEGORY_COLOR,
      }))
      .filter(r => r.amount > 0)
      .sort((a, b) => b.amount - a.amount)
  }, [transactions, categories])

  const categoryTotal = categoryRows.reduce((s, r) => s + r.amount, 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle>支払い割合</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Payer bar — monochrome shades to avoid clashing with category colors */}
        <div className="space-y-2">
          <div className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-muted">
            {payers.map((p, i) => {
              const pct = (p.paid / total) * 100
              return (
                <div
                  key={p.paidBy}
                  className={cn(payerShade(i))}
                  style={{
                    width: `${pct}%`,
                    minWidth: pct > 0 ? '2px' : undefined,
                  }}
                  title={`${displayName(p)}: ${formatAmount(p.paid)} (${pct.toFixed(1)}%)`}
                />
              )
            })}
          </div>

          <ul className="space-y-1">
            {payers.map((p, i) => {
              const pct = (p.paid / total) * 100
              return (
                <li
                  key={p.paidBy}
                  className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-1 py-1 text-sm"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className={cn('inline-block h-2 w-1 shrink-0 rounded-sm', payerShade(i))}
                      aria-hidden
                    />
                    <Avatar className="size-5 shrink-0">
                      {p.avatarUrl && <AvatarImage src={p.avatarUrl} alt={p.name ?? ''} />}
                      <AvatarFallback className="text-[10px]">{initial(p.name)}</AvatarFallback>
                    </Avatar>
                    <span className="truncate font-medium">{displayName(p)}</span>
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {pct.toFixed(1)}%
                  </span>
                  <span className="min-w-[64px] text-right tabular-nums">
                    {formatAmount(p.paid)}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>

        {/* Category bar — uses category colors */}
        {categoryRows.length > 0 && categoryTotal > 0 && (
          <div className="space-y-2 border-t pt-3">
            <div className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-muted">
              {categoryRows.map(r => {
                const pct = (r.amount / categoryTotal) * 100
                return (
                  <div
                    key={r.name}
                    style={{
                      width: `${pct}%`,
                      minWidth: pct > 0 ? '2px' : undefined,
                      backgroundColor: r.color,
                    }}
                    title={`${r.name}: ${formatAmount(r.amount)} (${pct.toFixed(1)}%)`}
                  />
                )
              })}
            </div>
            <ul className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {categoryRows.map(r => (
                <li key={r.name} className="flex items-center gap-1.5 text-xs">
                  <span
                    className="inline-block size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: r.color }}
                    aria-hidden
                  />
                  <span className="font-medium">{r.name}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {formatAmount(r.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-between border-t pt-2 text-sm">
          <span className="text-muted-foreground">合計</span>
          <span className="font-medium tabular-nums">{formatAmount(total)}</span>
        </div>
      </CardContent>
    </Card>
  )
}

function SettlementCard({
  payers,
  transfers,
}: {
  payers: PayerSummary[]
  transfers: ReturnType<typeof computeSettlement>['transfers']
}) {
  const share = payers[0]?.share ?? 0
  const payerById = new Map(payers.map(p => [p.paidBy, p]))

  return (
    <Card>
      <CardHeader>
        <CardTitle>割り勘の精算</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          1人あたり <span className="font-medium text-foreground">{formatAmount(share)}</span>
          （{payers.length}人で均等割り）
        </p>

        {transfers.length === 0 ? (
          <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            すでに均等に支払われています。精算は不要です。
          </p>
        ) : (
          <ul className="space-y-2">
            {transfers.map((t, i) => (
              <li
                key={i}
                className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <PayerChip
                    name={t.fromName}
                    avatarUrl={payerById.get(t.from)?.avatarUrl ?? null}
                  />
                  <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" />
                  <PayerChip
                    name={t.toName}
                    avatarUrl={payerById.get(t.to)?.avatarUrl ?? null}
                  />
                </div>
                <span className="shrink-0 font-medium tabular-nums">
                  {formatAmount(t.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function PayerChip({
  name,
  avatarUrl,
}: {
  name: string | null
  avatarUrl: string | null
}) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <Avatar className="size-5 shrink-0">
        {avatarUrl && <AvatarImage src={avatarUrl} alt={name ?? ''} />}
        <AvatarFallback className="text-[10px]">{initial(name)}</AvatarFallback>
      </Avatar>
      <span className="truncate">{name ?? '未設定'}</span>
    </span>
  )
}
